import React from "react";
import { formatterDate } from "../data/misc";
import { parseRawData } from "../data/ballBankParser";
import { faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import requestEsriModules from "@mrwa/rime-replacement/infrastructure/esriModules";
import classNames from "classnames";
import {
  ApplicationMessage,
  messageState,
} from "@mrwa/rime-replacement/esriControls/general/ApplicationMessage";
import { loadFileAsText } from "../data/misc";

import "./ballBank.css";
import { FileType } from "../data/fileType";

const FeatureSource = { BallBank: "BB", CurveSurvey: "CS" };

export default class BallBank extends React.Component {
  constructor(props) {
    super(props);
    this.videoSelections = [];
    this.ballBankFeatures = [];
    this.state = {
      selectedRun: null,
      curveSpeed: null,
      currentBallBankFeatures: [],
    };

    this.fileInput = React.createRef();

    this.fileUpload = this.fileUpload.bind(this);
    this.handleSelectRun = this.handleSelectRun.bind(this);
    this.handleSelectFiles = this.handleSelectFiles.bind(this);
    this.updateCurrentBallBankFeatures =
      this.updateCurrentBallBankFeatures.bind(this);
  }

  componentDidMount() {
    if (this.props.playerContext) {
      this.props.playerContext.onContextStateChanged(
        this.playerStateChanged.bind(this)
      );
    }
  }
  async componentDidUpdate(prevProps) {
    // re-calculate the curve speed when gps point changes
    if (this.props.referencePoint !== prevProps.referencePoint) {
      this.calculateCurveSpeed(this.props.referencePoint);
    }

    // bind new player context changes to monitor playlist state
    if (
      this.props.playerContext !== prevProps.playerContext &&
      this.props.playerContext
    ) {
      this.props.playerContext.onContextStateChanged(
        this.playerStateChanged.bind(this)
      );
    }

    if (
      this.props.bufferedPoint &&
      this.props.bufferedPoint !== prevProps.bufferedPoint
    ) {
      await this.updateCurrentBallBankFeatures();
    }
  }

  /**
   * listen to any playlist changes to determine if a new playlists
   * has been loaded and clear ballbank data if this occurs
   */
  playerStateChanged(state) {
    //check video mappings against existing video mappings to detemine changes
    const hasPlaylistChanged =
      state.videoToGpsFileMapping.length !== this.videoSelections.length ||
      !state.videoToGpsFileMapping.every((mapping) =>
        this.videoSelections.some(
          (vid) => vid.video === mapping.video && vid.gps === mapping.gps
        )
      );
    if (hasPlaylistChanged) {
      this.videoSelections = state.videoToGpsFileMapping;
      this.ballBankFeatures = [];
      this.setState({
        currentBallBankFeatures: [],
      });
    }
  }

  /**
   * filter the full list of ballbank features to a current list of
   * those features that fall within within the buffered point
   */
  async updateCurrentBallBankFeatures() {
    if (this.ballBankFeatures.length > 0 && this.props.bufferedPoint) {
      const { GeometryEngine } = await requestEsriModules({
        GeometryEngine: "esri/geometry/geometryEngineAsync",
      });

      const filterFeaturesNearPoint = await Promise.all(
        this.ballBankFeatures.map(async (feature) =>
          (await GeometryEngine.within(
            feature.geometry,
            this.props.bufferedPoint
          ))
            ? feature
            : null
        )
      );
      const featuresNearPoint = filterFeaturesNearPoint.filter(
        (feature) => feature
      );

      this.setState({
        currentBallBankFeatures: featuresNearPoint,
      });
    }
  }

  /**
   * determine the curve speed OF the features on the selected run closest to the
   * given reference point and interrogating the points safe speed attribute
   * @param {object} referencePoint - point at which to get the curve speed
   * @param {object} overrideRunId - runId to use, overriding the current selected run id
   */
  async calculateCurveSpeed(referencePoint, overrideRun) {
    const selectedRun = overrideRun
      ? JSON.parse(overrideRun)
      : this.processRuns().selectedRun;
    if (!selectedRun) {
      this.setState({ curveSpeed: null });
      return;
    }

    const { GeometryEngine } = await requestEsriModules({
      GeometryEngine: "esri/geometry/geometryEngineAsync",
    });

    let features =
      selectedRun.source === FeatureSource.CurveSurvey
        ? this.props.curveSurveyFeatures.filter(
            (feature) =>
              this.getRunId(feature, FeatureSource.CurveSurvey) ===
              selectedRun.id
          )
        : this.state.currentBallBankFeatures.filter(
            (feature) =>
              this.getRunId(feature, FeatureSource.BallBank) === selectedRun.id
          );

    //determine current curve speed
    const featureDistances = await Promise.all(
      features.map(
        async (feature) =>
          await GeometryEngine.distance(referencePoint, feature.geometry)
      )
    );
    const curveFeature =
      features[featureDistances.indexOf(Math.min(...featureDistances))];

    const { safeSpeedAttribute } = this.props.curveSurveyLayerConfig;
    this.setState({ curveSpeed: curveFeature.attributes[safeSpeedAttribute] });
  }

  handleSelectRun(e) {
    this.setState({ selectedRun: JSON.parse(e.target.value) });
    this.calculateCurveSpeed(this.props.referencePoint, e.target.value);
  }

  handleSelectFiles() {
    this.fileInput.current.click();
  }

  /**
   * generate a run identity from the features attributes
   * @param {object} feature - feature from which to retreive attributes
   * @param {string} suffix - suffix to the run Id representing source of the point
   * @returns - string representing the identity of the run
   */
  getRunId(feature, suffix) {
    const { runDateAttribute, runNameAttribute } =
      this.props.curveSurveyLayerConfig;

    return `${feature.attributes[runNameAttribute]} ${formatterDate.format(
      new Date(feature.attributes[runDateAttribute])
    )} [${suffix}]`;
  }

  /**
   * process any curve survey runs and loaded ball bank runs to determine
   * possible runId's to display in the select control for this current point
   */
  processRuns() {
    if (
      !this.props.curveSurveyFeatures &&
      this.state.currentBallBankFeatures.length === 0
    )
      return { possibleRuns: [], selectedRun: null };

    const { runDateAttribute } = this.props.curveSurveyLayerConfig;

    // convert feature objects into runid from road_name and curve_date atrributes
    const mapFeaturesToRunIds = (features, source) => {
      const allRunIds = (features ?? [])
        .sort(
          (f1, f2) =>
            new Date(f2.attributes[runDateAttribute]) -
            new Date(f1.attributes[runDateAttribute])
        )
        .map((sortedFeatures) => this.getRunId(sortedFeatures, source));
      const uniqueRunIds = [...new Set(allRunIds)];
      return uniqueRunIds.map((id) => ({ id: id, source: source }));
    };

    const possibleRuns = [
      ...mapFeaturesToRunIds(
        this.props.curveSurveyFeatures,
        FeatureSource.CurveSurvey
      ),
      ...mapFeaturesToRunIds(
        this.state.currentBallBankFeatures,
        FeatureSource.BallBank
      ),
    ];

    //const possibleRuns = [...new Set(combinedFeaturesRuns)];
    const selectedRun =
      possibleRuns.find(
        (run) =>
          this.state.selectedRun &&
          run.id === this.state.selectedRun.id &&
          run.source === this.state.selectedRun.source
      ) ?? (possibleRuns.length ? possibleRuns[0] : null);

    return { possibleRuns, selectedRun };
  }

  /**
   * upload ballbank files, read and format the file data and store in state
   */
  async fileUpload(e) {
    const isTXT = (type) => {
      const result = type.toUpperCase().endsWith(".TXT") ? true : false;
      return result;
    };

    const invalidFiles = [];

    // read ball bank files into features
    const ballBankFeatures = await Promise.all(
      Array.from(e.target.files).map(async (file, i) => {
        if (isTXT(file.name)) {
          const fileContents = await loadFileAsText(FileType.FileSystem(file));
          const featureData = parseRawData(fileContents);
          if (!featureData || featureData.length === 0)
            invalidFiles.push(file.name);
          return featureData;
        } else {
          invalidFiles.push(file.name);
        }
      })
    );

    //filter errored features (null)
    const validBallBankFeatures = ballBankFeatures.filter(
      (feature) => feature && feature.length > 0
    );

    // flatten into an array of point features
    const flattenedFeatures = validBallBankFeatures
      .flat()
      .map((run) => run.getFlattenedRun())
      .reduce((runPoints, currentRun) => {
        return runPoints.concat(currentRun);
      }, []);

    // filter out points on a run that already exist in state
    const pointFeatures = flattenedFeatures
      .map((feature) =>
        this.ballBankFeatures.some(
          (f) =>
            f.coordinates.latitude === feature.coordinates.latitude &&
            f.coordinates.longitude === feature.coordinates.longitude &&
            f.attributes.curve_date === feature.attributes.curve_date &&
            f.attributes.road_name === feature.attributes.road_name
        )
          ? null
          : feature
      )
      .filter((pt) => pt);

    const { Point } = await requestEsriModules({
      Point: "esri/geometry/Point",
    });

    // generate esri point geometries for each point feature
    const ballBankPoints = await Promise.all(
      pointFeatures.map(async (pt) => {
        return {
          geometry: await mapHelper.projectToView(
            new Point({
              latitude: pt.coordinates.latitude,
              longitude: pt.coordinates.longitude,
              spatialReference: { wkid: 4326 }, //4326 is WGS84, spatial reference used by GPS systemslongitude,
            })
          ),
          coordinates: pt.coordinates,
          attributes: pt.attributes,
        };
      })
    );

    this.ballBankFeatures = this.ballBankFeatures.concat(ballBankPoints);
    this.updateCurrentBallBankFeatures();

    if (invalidFiles.length > 0) {
      messageState.message = ApplicationMessage.Error(
        `Could not read all BallBank files. ${
          e.target.files.length - invalidFiles.length
        } of ${e.target.files.length} files loaded.`
      );
    }
  }

  render() {
    if (!this.props.curveSurveyLayerConfig) return null;

    const { possibleRuns, selectedRun } = this.processRuns();
    const highlightUnsafeSpeed =
      this.state.curveSpeed != null &&
      this.props.speedLimit != null &&
      this.props.curveSurveyLayerConfig.speedWarningThresholdKph != null &&
      this.props.speedLimit - this.state.curveSpeed >=
        this.props.curveSurveyLayerConfig.speedWarningThresholdKph;
    return this.props.curveSurveyLoading ? (
      <div>Loading Data...</div>
    ) : (
      <div className="ballbank">
        <div className="formInput">
          <label htmlFor="runSelect">Ballbank Source:</label>
          <div className="formInputRunControls">
            <select
              id="runSelect"
              value={selectedRun ? JSON.stringify(selectedRun) : ""}
              title={selectedRun ? selectedRun.id : ""}
              onChange={this.handleSelectRun}
            >
              {possibleRuns.map((run) => (
                <option
                  key={run.id}
                  value={JSON.stringify(run)}
                  className={
                    run.source === FeatureSource.BallBank ? "ballbankRun" : null
                  }
                >
                  {run.id}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={this.handleSelectFiles}
              title="Select Ballbank Files"
              className="light"
              disabled={this.videoSelections.length === 0}
            >
              <FontAwesomeIcon icon={faFolderOpen} size="1x" />
            </button>
            <input
              type="file"
              accept=".TXT"
              multiple
              onChange={this.fileUpload}
              style={{ display: "none" }}
              ref={this.fileInput}
            ></input>
          </div>
        </div>
        <div className="horizontalGrid">
          <div className="gridItem">
            <label htmlFor="curveSpeed">Advisory Speed</label>
          </div>
          <div
            className={classNames("gridItem", "emphasis", {
              highlight: highlightUnsafeSpeed,
            })}
          >
            <span id="curveSpeed">
              {this.state.curveSpeed != null
                ? `${this.state.curveSpeed}km/h`
                : null}
            </span>
          </div>
        </div>
      </div>
    );
  }
}
